import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  IoRefresh,
  IoAdd,
  IoTrash,
  IoCalendar,
  IoPerson,
  IoCreate,
  IoClose,
  IoSearch,
  IoCheckmark,
} from 'react-icons/io5';
import {
  createVisit,
  deleteVisit,
  fetchOverdueVisits,
  fetchVisits,
} from '../api/api';
import { useAuth } from '../context/AuthContext';
import './AdminVisits.css';

const AdminVisits = () => {
  const { users, getAllUsers, user: currentUser } = useAuth();
  
  // Check if current user is area head
  const userRole = (currentUser?.role || '').toLowerCase();
  const isAreaHead = userRole === 'area_head' || userRole === 'area head';
  const currentUserId = currentUser?.id;
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Form state
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedEngineer, setSelectedEngineer] = useState(null);
  const [visitDate, setVisitDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Selection modals
  const [showUserModal, setShowUserModal] = useState(false);
  const [showEngineerModal, setShowEngineerModal] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [engineerSearch, setEngineerSearch] = useState('');

  // Filters
  const [filter, setFilter] = useState('all');

  // Get AMC users assigned to area head for scheduling visits - MUST be defined before loadVisits
  const scheduleUsers = useMemo(() => {
    console.log('Total users:', users.length, 'isAreaHead:', isAreaHead, 'currentUserId:', currentUserId);

    // First filter by AMC (check both camelCase and snake_case)
    let filtered = users.filter((u) => {
      const siteType = String(u.siteType || u.site_type || '').toLowerCase();
      const isAmc = siteType.includes('amc');
      console.log('User:', u.name, 'siteType:', siteType, 'isAMC:', isAmc);
      return isAmc;
    });

    // If area head, further filter to only their assigned AMC users
    if (isAreaHead && currentUserId) {
      filtered = filtered.filter((u) => {
        const userAreaHeadId = String(u.areaHeadId || u.area_head_id || '');
        const match = userAreaHeadId === String(currentUserId);
        console.log('AMC User:', u.name, 'areaHeadId:', userAreaHeadId, 'match:', match);
        return match;
      });
    }

    console.log('Filtered AMC users:', filtered.length);
    return filtered;
  }, [users, isAreaHead, currentUserId]);

  // Get engineers associated with area head (not all engineers) - MUST be defined before loadVisits
  const engineers = useMemo(() => {
    let filtered = users.filter((u) => {
      const role = (u.role || '').toLowerCase();
      return role === 'engineer';
    });

    // If area head, filter to only their assigned engineers
    if (isAreaHead && currentUserId) {
      filtered = filtered.filter((u) => {
        const userAreaHeadId = String(u.areaHeadId || u.area_head_id || '');
        const match = userAreaHeadId === String(currentUserId);
        console.log('Engineer:', u.name, 'areaHeadId:', userAreaHeadId, 'match:', match);
        return match;
      });
    }

    console.log('Filtered engineers:', filtered.length);
    return filtered;
  }, [users, isAreaHead, currentUserId]);

  const loadVisits = useCallback(async () => {
    setLoading(true);
    try {
      let data;
      console.log('Loading visits with filter:', filter, 'isAreaHead:', isAreaHead);
      if (filter === 'overdue') {
        data = await fetchOverdueVisits();
      } else {
        const params = {};
        if (filter === 'upcoming') {
          params.startDate = new Date().toISOString().split('T')[0];
          params.status = 'scheduled';
        } else if (filter === 'completed') {
          params.status = 'completed';
        }
        console.log('Fetching visits with params:', params);
        data = await fetchVisits(params);
      }

      // If area head, filter visits to only show their assigned users' visits
      if (isAreaHead && currentUserId && data) {
        const scheduleUserIds = new Set(scheduleUsers.map(u => String(u.id)));
        data = data.filter((visit) => {
          const visitUserId = String(visit.userId || visit.user_id || '');
          const match = scheduleUserIds.has(visitUserId);
          console.log('Visit filter - userId:', visitUserId, 'match:', match);
          return match;
        });
      }

      console.log('Visits data received:', data);
      console.log('Visits count:', data?.length || 0);
      setVisits(data || []);
    } catch (err) {
      console.error('Failed to load visits', err);
      console.error('Error details:', err.message);
    } finally {
      setLoading(false);
    }
  }, [filter, isAreaHead, currentUserId, scheduleUsers]);

  useEffect(() => {
    loadVisits();
    // Load users if not already loaded
    if (users.length === 0) {
      getAllUsers();
    }
  }, [loadVisits, users.length, getAllUsers]);

  const handleCreateVisit = async () => {
    if (!selectedUser || !selectedEngineer) {
      alert('Please select user and engineer');
      return;
    }

    setSubmitting(true);
    try {
      await createVisit({
        userId: selectedUser.id,
        engineerId: selectedEngineer.id,
        visitDate: visitDate,
        notes,
      });
      alert('Visit scheduled successfully');
      setShowCreateModal(false);
      resetForm();
      loadVisits();
    } catch (err) {
      alert(err?.message || 'Failed to schedule visit');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedUser(null);
    setSelectedEngineer(null);
    setVisitDate(new Date().toISOString().split('T')[0]);
    setNotes('');
    setUserSearch('');
    setEngineerSearch('');
  };

  const handleDeleteVisit = async (visitId) => {
    if (!visitId) return;
    if (!window.confirm('Are you sure you want to delete this visit?')) return;

    try {
      await deleteVisit(visitId);
      loadVisits();
    } catch (err) {
      alert(err?.message || 'Failed to delete visit');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#4CAF50';
      case 'scheduled':
        return '#F57C00';
      case 'overdue':
        return '#F44336';
      case 'cancelled':
        return '#999999';
      default:
        return '#666666';
    }
  };

  const filteredScheduleUsers = scheduleUsers.filter(
    (u) =>
      u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
      u.siteName?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const filteredEngineers = engineers.filter(
    (e) =>
      e.name?.toLowerCase().includes(engineerSearch.toLowerCase()) ||
      e.phone?.toLowerCase().includes(engineerSearch.toLowerCase())
  );

  return (
    <div className="admin-visits">
      <div className="visits-header">
        <div className="header-title">
          <h1>AMC Visits</h1>
          <p>Preventive Maintenance</p>
        </div>
        <div className="header-actions">
          <button className="refresh-btn" onClick={loadVisits} title="Refresh visits">
            <IoRefresh />
          </button>
          <button className="add-btn" onClick={() => setShowCreateModal(true)}>
            <span>+</span> Schedule Visit
          </button>
        </div>
      </div>

      <div className="filter-row">
        {['all', 'upcoming', 'overdue', 'completed'].map((f) => (
          <button
            key={f}
            className={`filter-btn ${filter === f ? 'active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading-wrap">
          <div className="spinner"></div>
          <p>Loading visits...</p>
        </div>
      ) : visits.length === 0 ? (
        <div className="empty-wrap">
          <div className="empty-icon"><IoCalendar /></div>
          <h3>No visits found</h3>
          <p>No visits found for filter: <strong>{filter}</strong></p>
          <button className="retry-btn" onClick={loadVisits}>
            <IoRefresh /> Refresh
          </button>
        </div>
      ) : (
        <div className="visits-list">
          {visits.map((visit) => (
            <div
              key={visit.id}
              className="visit-card"
              onClick={() => {
                setSelectedVisit(visit);
                setShowDetailModal(true);
              }}
            >
              <div className="visit-header">
                <span
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(visit.status) + '20', color: getStatusColor(visit.status) }}
                >
                  {visit.status}
                </span>
                <button
                  className="delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteVisit(visit.id);
                  }}
                >
                  <IoTrash />
                </button>
              </div>

              <div className="visit-info">
                <h4>{visit.user_name || 'Unknown User'}</h4>
                <p className="site-info">{visit.site_name || 'No site name'}</p>
                <p className="site-address">{visit.site_address || 'No address'}</p>
              </div>

              <div className="visit-footer">
                <div className="footer-item">
                  <IoCalendar />
                  <span>{formatDate(visit.visit_date || visit.visitDate)}</span>
                </div>
                <div className="footer-item">
                  <IoPerson />
                  <span>{visit.engineer_name || 'Unassigned'}</span>
                </div>
              </div>
              
              {/* Created By */}
              {(visit.created_by_name || visit.created_by) && (
                <div className="visit-creator">
                  <IoCreate />
                  <span>Scheduled by: {visit.created_by_name || 'User #' + visit.created_by}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Visit Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Schedule Visit</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>
                <IoClose />
              </button>
            </div>

            <div className="modal-body">
              {/* User Selection */}
              <div className="form-section">
                <label>Select AMC User *</label>
                <div className="dropdown-trigger" onClick={() => setShowUserModal(true)}>
                  <span className={selectedUser ? 'selected' : 'placeholder'}>
                    {selectedUser ? selectedUser.name : 'Tap to select AMC user'}
                  </span>
                  {selectedUser?.siteType && <span className="amc-badge">{selectedUser.siteType}</span>}
                  <span className="chevron">›</span>
                </div>
                {selectedUser && <p className="dropdown-subtext">{selectedUser.siteName || 'No site name'}</p>}
              </div>

              {/* Engineer Selection */}
              <div className="form-section">
                <label>Assign Engineer *</label>
                <div className="dropdown-trigger" onClick={() => setShowEngineerModal(true)}>
                  <span className={selectedEngineer ? 'selected' : 'placeholder'}>
                    {selectedEngineer ? selectedEngineer.name : 'Tap to select engineer'}
                  </span>
                  <span className="chevron">›</span>
                </div>
                {selectedEngineer && <p className="dropdown-subtext">{selectedEngineer.phone || 'No phone'}</p>}
              </div>

              {/* Visit Date */}
              <div className="form-section">
                <label>Visit Date *</label>
                <input
                  type="date"
                  className="date-input"
                  value={visitDate}
                  onChange={(e) => setVisitDate(e.target.value)}
                />
              </div>

              {/* Notes */}
              <div className="form-section">
                <label>Notes</label>
                <textarea
                  className="notes-input"
                  placeholder="Enter visit notes"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>

              {/* Submit */}
              <button
                className={`schedule-btn ${submitting ? 'disabled' : ''}`}
                onClick={handleCreateVisit}
                disabled={submitting}
              >
                {submitting ? (
                  <span className="spinner-small"></span>
                ) : (
                  <>
                    <span className="btn-icon"><IoCheckmark /></span>
                    <span className="btn-text">
                      <strong>Schedule Visit</strong>
                      <small>Create new maintenance visit</small>
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Selection Modal */}
      {showUserModal && (
        <div className="modal-overlay" onClick={() => setShowUserModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Select AMC User</h2>
              <button className="close-btn" onClick={() => setShowUserModal(false)}>
                <IoClose />
              </button>
            </div>

            <div className="search-container">
              <IoSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search AMC users..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
              />
              {userSearch && (
                <button className="clear-btn" onClick={() => setUserSearch('')}>
                  <IoClose />
                </button>
              )}
            </div>

            <div className="modal-body user-list">
              {filteredScheduleUsers.length === 0 ? (
                <p className="no-data">No users found</p>
              ) : (
                filteredScheduleUsers.map((user) => (
                  <div
                    key={user.id}
                    className={`user-item ${selectedUser?.id === user.id ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedUser(user);
                      setShowUserModal(false);
                      setUserSearch('');
                    }}
                  >
                    <div className="user-item-row">
                      <span className="user-name">{user.name}</span>
                      <span className="amc-badge-small">AMC</span>
                    </div>
                    <span className="user-site">{user.siteName || 'No site'}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Engineer Selection Modal */}
      {showEngineerModal && (
        <div className="modal-overlay" onClick={() => setShowEngineerModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Select Engineer</h2>
              <button className="close-btn" onClick={() => setShowEngineerModal(false)}>
                <IoClose />
              </button>
            </div>

            <div className="search-container">
              <IoSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search engineers..."
                value={engineerSearch}
                onChange={(e) => setEngineerSearch(e.target.value)}
              />
              {engineerSearch && (
                <button className="clear-btn" onClick={() => setEngineerSearch('')}>
                  <IoClose />
                </button>
              )}
            </div>

            <div className="modal-body user-list">
              {filteredEngineers.length === 0 ? (
                <p className="no-data">No engineers found</p>
              ) : (
                filteredEngineers.map((engineer) => (
                  <div
                    key={engineer.id}
                    className={`user-item ${selectedEngineer?.id === engineer.id ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedEngineer(engineer);
                      setShowEngineerModal(false);
                      setEngineerSearch('');
                    }}
                  >
                    <span className="user-name">{engineer.name}</span>
                    <span className="user-phone">{engineer.phone || 'No phone'}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedVisit && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Visit Details</h2>
              <button className="close-btn" onClick={() => setShowDetailModal(false)}>
                <IoClose />
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <label>Status</label>
                <span
                  className="status-badge"
                  style={{
                    backgroundColor: getStatusColor(selectedVisit.status) + '20',
                    color: getStatusColor(selectedVisit.status),
                  }}
                >
                  {selectedVisit.status}
                </span>
              </div>

              <div className="detail-section">
                <label>User Information</label>
                <p className="detail-value">{selectedVisit.user_name}</p>
                <p className="detail-subvalue">{selectedVisit.user_phone}</p>
              </div>

              <div className="detail-section">
                <label>Site Details</label>
                <p className="detail-value">{selectedVisit.site_name}</p>
                <p className="detail-subvalue">{selectedVisit.site_address}</p>
                {(selectedVisit.state || selectedVisit.area) && (
                  <p className="detail-subvalue">
                    {[selectedVisit.area, selectedVisit.state].filter(Boolean).join(', ')}
                  </p>
                )}
              </div>

              <div className="detail-section">
                <label>Visit Date</label>
                <p className="detail-value">{formatDate(selectedVisit.visit_date || selectedVisit.visitDate)}</p>
              </div>

              {selectedVisit.notes && (
                <div className="detail-section">
                  <label>Admin Notes</label>
                  <p className="detail-value">{selectedVisit.notes}</p>
                </div>
              )}

              {selectedVisit.engineer_name && (
                <div className="detail-section">
                  <label>Assigned Engineer</label>
                  <p className="detail-value">{selectedVisit.engineer_name}</p>
                  {selectedVisit.engineer_phone && (
                    <p className="detail-subvalue">{selectedVisit.engineer_phone}</p>
                  )}
                </div>
              )}

              {/* Created By */}
              {(selectedVisit.created_by_name || selectedVisit.created_by) && (
                <div className="detail-section">
                  <label>Scheduled By</label>
                  <p className="detail-value">{selectedVisit.created_by_name || 'User #' + selectedVisit.created_by}</p>
                </div>
              )}

              {selectedVisit.status === 'completed' && (
                <>
                  <div className="detail-section">
                    <label>Completed At</label>
                    <p className="detail-value">
                      {formatDate(selectedVisit.completed_at || selectedVisit.completedAt)}
                    </p>
                  </div>
                  {selectedVisit.completion_notes && (
                    <div className="detail-section">
                      <label>Completion Notes</label>
                      <p className="detail-value">{selectedVisit.completion_notes}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVisits;
